import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Button,
  Paper,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Store as StoreIcon,
  Circle as OnlineIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Conversation } from '../../services/messageService';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { getCachedProductThumbnailSrc } from '../../utils/productThumbnailCache';

interface ChatInfoPopupProps {
  open: boolean;
  onClose: () => void;
  conversation: Conversation | null;
}

interface UserInfo {
  id: string;
  first_name: string;
  last_name?: string;
  alias?: string;
}

const ChatInfoPopup: React.FC<ChatInfoPopupProps> = ({ open, onClose, conversation }) => {
  const { user } = useAuthStore();
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [partnerUserInfo, setPartnerUserInfo] = React.useState<UserInfo | null>(null);
  const [currentUserInfo, setCurrentUserInfo] = React.useState<UserInfo | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [productThumbSrc, setProductThumbSrc] = React.useState<string | null>(null);

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy ID:', error);
    }
  };

  // Fetch user information from the users table
  const fetchUserInfo = async () => {
    if (!conversation || !user) return;
    
    setLoading(true);
    try {
      const otherUserId = conversation.other_user?.id;
      
      // Fetch partner user info
      if (otherUserId) {
        const { data: partnerData, error: partnerError } = await supabase
          .from('users')
          .select('id, first_name, last_name, alias')
          .eq('id', otherUserId)
          .single();
        
        if (partnerError) {
          console.error('Error fetching partner user info:', partnerError);
        } else {
          setPartnerUserInfo(partnerData);
        }
      }
      
      // Fetch current user info
      const { data: currentData, error: currentError } = await supabase
        .from('users')
        .select('id, first_name, last_name, alias')
        .eq('id', user.id)
        .single();
      
      if (currentError) {
        console.error('Error fetching current user info:', currentError);
      } else {
        setCurrentUserInfo(currentData);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user info when popup opens
  React.useEffect(() => {
    if (open && conversation) {
      fetchUserInfo();
    }
  }, [open, conversation]);

  React.useEffect(() => {
    if (!open || !conversation) {
      setProductThumbSrc(null);
      return;
    }

    const productId = String((conversation as any)?.product_id || conversation.product?.id || '').trim();
    if (!productId) {
      setProductThumbSrc(null);
      return;
    }

    const cached = getCachedProductThumbnailSrc(productId);
    if (cached) {
      setProductThumbSrc(cached);
      return;
    }

    const fallbackUrl = (conversation as any)?.product?.images?.[0];
    setProductThumbSrc(typeof fallbackUrl === 'string' && fallbackUrl.trim() ? fallbackUrl.trim() : null);
  }, [open, (conversation as any)?.product_id, conversation?.product?.id]);

  if (!conversation) return null;

  const otherUser = conversation.other_user;
  const currentUser = user;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight="bold" sx={{ 
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Chat Information
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                background: 'rgba(0,0,0,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, background: 'rgba(255, 255, 255, 0.6)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading user information...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Product Preview */}
            {(productThumbSrc || conversation.product || (conversation as any).product_id) && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'rgba(0,0,0,0.06)',
                      border: '1px solid rgba(0,0,0,0.08)',
                      flexShrink: 0,
                    }}
                  >
                    {productThumbSrc ? (
                      <Box
                        component="img"
                        src={productThumbSrc}
                        alt={conversation.product?.name || 'Product'}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : null}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 0.5 }}>
                      Product
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" noWrap>
                      {conversation.product?.name || conversation.subject || 'Product'}
                    </Typography>
                    {typeof (conversation.product as any)?.price === 'number' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 700 }}>
                        ${(conversation.product as any).price.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Other User Info */}
            <Paper elevation={2} sx={{ 
              p: 3, 
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(0,0,0,0.1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ 
                    width: 50, 
                    height: 50, 
                    bgcolor: 'primary.main',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}>
                    {(partnerUserInfo?.first_name || 'U').charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {partnerUserInfo?.first_name || 'Loading...'} (Partner)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label="Vendor"
                        size="small"
                        color="primary"
                        variant="outlined"
                        icon={<StoreIcon sx={{ fontSize: 16 }} />}
                      />
                      <OnlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Online
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    User ID:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 2,
                    background: 'rgba(0,0,0,0.05)',
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        wordBreak: 'break-all',
                        flex: 1
                      }}
                    >
                      {partnerUserInfo?.id || 'Loading...'}
                    </Typography>
                    <Tooltip title={copiedId === partnerUserInfo?.id ? "Copied!" : "Copy ID"}>
                      <IconButton
                        size="small"
                        onClick={() => partnerUserInfo?.id && handleCopyId(partnerUserInfo.id)}
                        sx={{ 
                          color: copiedId === partnerUserInfo?.id ? 'success.main' : 'text.secondary',
                          '&:hover': { 
                            background: 'rgba(0,0,0,0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {copiedId === partnerUserInfo?.id ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    First Name:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: partnerUserInfo?.first_name ? 'primary.main' : 'error.main',
                      fontSize: '1rem'
                    }}
                  >
                    {partnerUserInfo?.first_name || 'Loading...'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    Alias:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: partnerUserInfo?.alias ? 'success.main' : 'error.main',
                      fontSize: '1rem'
                    }}
                  >
                    {partnerUserInfo?.alias || 'NO ALIAS FOUND'}
                  </Typography>
                </Box>
              </Paper>

            {/* Current User Info */}
            <Paper elevation={2} sx={{ 
              p: 3, 
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(0,0,0,0.1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ 
                    width: 50, 
                    height: 50, 
                    bgcolor: 'secondary.main',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}>
                    {(currentUserInfo?.first_name || 'Y').charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {currentUserInfo?.first_name || 'Loading...'} (You)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label="Consumer"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        icon={<PersonIcon sx={{ fontSize: 16 }} />}
                      />
                      <OnlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Online
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    Your ID:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 2,
                    background: 'rgba(0,0,0,0.05)',
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        wordBreak: 'break-all',
                        flex: 1
                      }}
                    >
                      {currentUserInfo?.id || 'Loading...'}
                    </Typography>
                    <Tooltip title={copiedId === currentUserInfo?.id ? "Copied!" : "Copy ID"}>
                      <IconButton
                        size="small"
                        onClick={() => currentUserInfo?.id && handleCopyId(currentUserInfo.id)}
                        sx={{ 
                          color: copiedId === currentUserInfo?.id ? 'success.main' : 'text.secondary',
                          '&:hover': { 
                            background: 'rgba(0,0,0,0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {copiedId === currentUserInfo?.id ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    First Name:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: currentUserInfo?.first_name ? 'primary.main' : 'error.main',
                      fontSize: '1rem'
                    }}
                  >
                    {currentUserInfo?.first_name || 'Loading...'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    Alias:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: currentUserInfo?.alias ? 'success.main' : 'error.main',
                      fontSize: '1rem'
                    }}
                  >
                    {currentUserInfo?.alias || 'NO ALIAS FOUND'}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default ChatInfoPopup;
